import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ChoiceCard, StepAlert, SummaryRow, ToggleRow, PrimaryButton, useInstallTheme } from "../ui";
import ContactAccessTable from "../../claim/ContactAccessTable";
import { deviceTestLabel } from "../deviceTest";

export function ClaimGate({ routing, overview, hasStoredSession, onSignIn, onStart, busy, claimCode }) {
  const { t } = useInstallTheme();
  return (
    <Box>
      <Box p={2.5} borderRadius="16px" mb={2} sx={{ background: t.softSurface, border: `1px solid ${t.borderSubtle}` }}>
        {overview?.device ? (
          <>
            <Typography fontWeight={700}>{overview.device.name || `Device #${overview.device.device_id}`}</Typography>
            {overview.model?.name ? (
              <Typography variant="body2" sx={{ color: t.textMuted }}>
                Model: {overview.model.name}
              </Typography>
            ) : null}
          </>
        ) : (
          <Typography fontWeight={700}>Monitor {claimCode}</Typography>
        )}
        {routing?.provider ? (
          <Typography variant="body2" sx={{ color: t.textMuted, mt: 0.5 }}>
            From: {routing.provider}
          </Typography>
        ) : null}
      </Box>
      {routing && routing.available === false && !hasStoredSession && (
        <StepAlert severity="warning">This claim is no longer available.</StepAlert>
      )}
      <PrimaryButton t={t} loading={busy} disabled={!routing?.available && !hasStoredSession} onClick={onStart}>
        {hasStoredSession ? "Resume installation" : "Start installation"}
      </PrimaryButton>
      <Box mt={2}>
        <ChoiceCard
          title="Sign in"
          description="Installer sign-in is required before a claim can start."
          onClick={onSignIn}
          t={t}
        />
      </Box>
    </Box>
  );
}

function Heading({ children, t }) {
  return (
    <Typography variant="subtitle2" fontWeight={700} sx={{ color: t.textStrong, mt: 3, mb: 0.5 }}>
      {children}
    </Typography>
  );
}

export function InstallationReviewStep({ state, summary, onEdit, dispatch, t, termsVersion, blockers = [] }) {
  const {
    billingContact, customer, addressName, addressLabel, assetName, assetDetails, deviceId,
    accessRows, recipients, providerNames, calibrationViaOperation, deviceTestAvailable,
  } = summary;
  const configured = state.thresholds.filter((r) => String(r.threshold_value ?? "").trim() !== "");
  const assetStep = state.branch.assetMode === "existing" ? "asset_select" : "asset_new";
  return (
    <Box>
      {blockers.map((message) => (
        <StepAlert key={message} severity="error">
          {message}
        </StepAlert>
      ))}
      <Heading t={t}>Customer</Heading>
      <SummaryRow label="Customer name" value={customer.name} onEdit={() => onEdit("customer_mode")} t={t} />
      <SummaryRow label="Entity type" value={customer.entityType} t={t} />
      <SummaryRow label="Company" value={customer.company} t={t} />
      <SummaryRow label="Status" value={customer.status} t={t} />
      {customer.id ? <SummaryRow label="Customer ID" value={String(customer.id)} t={t} /> : null}

      <Heading t={t}>Service address</Heading>
      {addressLabel ? <SummaryRow label="Service / property label" value={addressLabel} t={t} /> : null}
      <SummaryRow label="Street address" value={addressName} onEdit={() => onEdit(state.branch.addressMode === "new" ? "address_new" : "address_mode")} t={t} />

      <Heading t={t}>Contacts & device access</Heading>
      <SummaryRow
        label="Contacts"
        value={state.contacts.map((c) => `${c.first_name} ${c.last_name}`.trim()).filter(Boolean).join(", ") || "No new contacts"}
        onEdit={() => onEdit("contacts")}
        t={t}
      />
      <SummaryRow label="Future billing contact" value={billingContact || "Not selected"} t={t} />
      <Box mt={1}>
        <ContactAccessTable rows={accessRows} readOnly />
      </Box>

      <Heading t={t}>Tank / asset & device</Heading>
      <SummaryRow label="Tank / asset" value={assetName} onEdit={() => onEdit(assetStep)} t={t} />
      {assetDetails ? <SummaryRow label="Asset details" value={assetDetails} t={t} /> : null}
      <SummaryRow label="Asset identifier" value={state.device.map_identifier} onEdit={() => onEdit("device")} t={t} />
      <SummaryRow label="Device name" value={state.device.device_name} onEdit={() => onEdit("device")} t={t} />
      <SummaryRow label="Installation date" value={state.device.installation_date} onEdit={() => onEdit("device")} t={t} />
      {state.tankGeometry ? (
        <>
          <Heading t={t}>Tank size selected with the helper</Heading>
          <SummaryRow label="Shape" value={state.tankGeometry.shape}
            onEdit={() => onEdit("calibration")} t={t} />
          {Object.entries(state.tankGeometry.values).map(([key, value]) => (
            <SummaryRow key={key}
              label={({
                length_in: "Length", width_in: "Width", depth_in: "Inside depth",
                diameter_in: "Diameter", capacity_gallons: "Full capacity",
              })[key]}
              value={`${Number(value).toFixed(2)} ${key === "capacity_gallons" ? "US gal" : "in"}`}
              t={t} />
          ))}
          <SummaryRow label="Measurement source"
            value={`Dimensions ${state.tankGeometry.dimensionSource}; capacity ${state.tankGeometry.capacitySource}`}
            t={t} />
          {state.tankGeometry.assumedEqualSides ? (
            <StepAlert severity="warning">Equal length and width were assumed.</StepAlert>
          ) : null}
        </>
      ) : null}
      {Number(state.deviceTypeId) === 4 ? (
        <>
          <Heading t={t}>Sensor calibration</Heading>
          <SummaryRow
            label="Calibration"
            value={!state.calibration.enabled
              ? "Skipped - leave any existing calibration unchanged"
              : calibrationViaOperation
                ? "Saved as an installation setup item after device assignment"
                : "Will save after device assignment"}
            onEdit={() => onEdit("calibration")}
            t={t}
          />
          {state.calibration.enabled ? (
            <>
              <SummaryRow label="Sensor mount"
                value={state.calibration.sensor_mount_type === "top" ? "Top of tank" : "Bottom-mounted"}
                t={t} />
              <SummaryRow label="Floor to sensor face"
                value={`${state.calibration.sensor_height_offset} in`} t={t} />
              <SummaryRow label="Empty point (0%)" value="0 in - bottom of tank" t={t} />
              <SummaryRow label="Full point (100%)"
                value={`${state.calibration.scale_max_eng} in - measured full height, usually bottom of inlet`}
                t={t} />
              {deviceTestAvailable || state.deviceTest ? (
                <SummaryRow label="Device test" value={deviceTestLabel(state.deviceTest)}
                  onEdit={() => onEdit("calibration")} t={t} />
              ) : null}
            </>
          ) : null}
        </>
      ) : null}
      <SummaryRow
        label="Thresholds"
        value={configured
          .map((r) => `${r.parameter_label} ${r.threshold_value}${r.unit ? ` ${r.unit}` : ""} (alert ${r.alert_enabled ? "on" : "off"}, event ${r.event_enabled ? "on" : "off"})`)
          .join("; ") || "None configured"}
        onEdit={() => onEdit("thresholds")}
        t={t}
      />
      <SummaryRow
        label="Alerts"
        value={state.alerts?.enabled
          ? `Alerts on${recipients.length ? ` - ${recipients.map((r) => `${r.label} (${r.kind === "sms" ? "text" : "email"} ${r.value})`).join(", ")}` : " - no recipients selected"}`
          : "Alerts off - notifications disabled"}
        onEdit={() => onEdit("notifications")}
        t={t}
      />

      <Heading t={t}>Provider sharing</Heading>
      <SummaryRow
        label="Providers (asset sharing)"
        value={providerNames.length ? `${providerNames.join(", ")} - selected; saved status shown after submission` : "Not selected"}
        onEdit={() => onEdit("providers")}
        t={t}
      />

      <Heading t={t}>Device assignment</Heading>
      <SummaryRow
        label="Will be assigned"
        value={`Device #${deviceId ?? "-"} -> ${customer.name} -> ${addressName || "-"} -> ${assetName || "-"}`}
        t={t}
      />
      <SummaryRow label="Subscription" value="The existing plan and verified included term follow this device; the installation date anchors the term." t={t} />

      <Box mt={3}>
        <ToggleRow
          label="I confirm the installation information is accurate."
          checked={state.attest.accuracy}
          onChange={(checked) => dispatch({ type: "SET_ATTEST", attest: { accuracy: checked } })}
        />
        <ToggleRow
          label={`I accept the installation terms${termsVersion ? ` (version ${termsVersion})` : ""}.`}
          checked={state.attest.terms}
          onChange={(checked) => dispatch({ type: "SET_ATTEST", attest: { terms: checked } })}
        />
      </Box>
      <StepAlert severity="warning">
        Confirming saves any new customer records, assigns this device to the asset, and completes the one-time claim.
        Provider sharing, device access, and alert rules are saved next and each result is shown separately.
        Calibration, if selected, is saved after device assignment and reports its own result and retry - it is not
        part of the one-time claim transaction.
        {state.deviceTest ? " The optional device test result is shown here for your review only; it is not yet recorded in the installation history." : ""}
      </StepAlert>
    </Box>
  );
}
